import React, { useState, useEffect, useCallback } from 'react';
import { usersAPI, convsAPI, authAPI } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { useChat } from '../../context/ChatContext';
import Avatar from '../UI/Avatar';

// ── NEW CHAT MODAL ────────────────────────────────────────────
export function NewChatModal({ onClose }) {
  const { startDirectChat }    = useChat();
  const [query, setQuery]      = useState('');
  const [results, setResults]  = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!query.trim() || query.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await usersAPI.search(query);
        if (res.data.success) setResults(res.data.data.users);
      } catch {}
      setSearching(false);
    }, 350);
    return () => clearTimeout(t);
  }, [query]);

  const start = async (userId) => {
    await startDirectChat(userId);
    onClose();
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2>💬 New Chat</h2>
          <button className="icon-btn dark" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <input className="form-input" placeholder="Search by name or phone number"
            value={query} onChange={e => setQuery(e.target.value)} autoFocus />
          <div style={{ marginTop: 12 }}>
            {searching && <div style={{ textAlign: 'center', padding: 12 }}><div className="spinner" style={{ width: 24, height: 24 }} /></div>}
            {results.map(u => (
              <div key={u.id}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', cursor: 'pointer', borderBottom: '1px solid var(--border)' }}
                onClick={() => start(u.id)}>
                <Avatar user={u} size={44} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 600 }}>{u.name}</div>
                  <div style={{ fontSize: 13, color: '#667781' }}>{u.phone}</div>
                  {u.about && <div style={{ fontSize: 12, color: '#999', marginTop: 2, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{u.about}</div>}
                </div>
                <span style={{ fontSize: 12, color: u.status === 'online' ? '#25D366' : '#667781' }}>
                  {u.status === 'online' ? '🟢 Online' : '⚫'}
                </span>
              </div>
            ))}
            {query.length >= 2 && !searching && results.length === 0 && (
              <p style={{ textAlign: 'center', color: '#667781', padding: 20 }}>No users found</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── NEW GROUP MODAL ───────────────────────────────────────────
export function NewGroupModal({ onClose }) {
  const { loadConversations, openConversation } = useChat();
  const [step, setStep]         = useState(1);
  const [query, setQuery]       = useState('');
  const [results, setResults]   = useState([]);
  const [selected, setSelected] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!query || query.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      const res = await usersAPI.search(query);
      if (res.data.success) {
        const users = res.data.data.users;
        setResults(users);
        
        // Auto-select if there's an exact match on phone or name
        const exact = users.find(u => u.phone === query || u.name.toLowerCase() === query.toLowerCase());
        if (exact && !selected.find(s => s.id === exact.id)) {
          setSelected(prev => [...prev, exact]);
        }
      }
    }, 350);
    return () => clearTimeout(t);
  }, [query]);

  const toggle = (u) => {
    setSelected(s => s.find(x => x.id === u.id) ? s.filter(x => x.id !== u.id) : [...s, u]);
  };

  const create = async () => {
    if (!groupName.trim() || selected.length === 0) return;
    setCreating(true);
    try {
      const res = await convsAPI.createGroup({ name: groupName, members: selected.map(u => u.id) });
      if (res.data.success) {
        await loadConversations();
        openConversation(res.data.data.conversation_id);
        onClose();
      }
    } catch {}
    setCreating(false);
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2>👥 {step === 1 ? 'Add Members' : 'Group Info'}</h2>
          <button className="icon-btn dark" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {step === 1 ? (
            <>
              {selected.length > 0 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                  {selected.map(u => (
                    <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#E8F5E9', padding: '4px 10px', borderRadius: 100, fontSize: 13 }}>
                      {u.name}
                      <button onClick={() => toggle(u)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#667781', fontSize: 16 }}>✕</button>
                    </div>
                  ))}
                </div>
              )}
              <input className="form-input" placeholder="Search people" value={query}
                onChange={e => setQuery(e.target.value)} autoFocus 
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && results.length > 0) {
                    e.preventDefault();
                    if (!selected.find(s => s.id === results[0].id)) {
                      toggle(results[0]);
                    }
                    setQuery('');
                  }
                }}
              />
              <div style={{ marginTop: 10, maxHeight: 260, overflowY: 'auto' }}>
                {results.map(u => {
                  const isSel = selected.find(x => x.id === u.id);
                  return (
                    <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 4px', cursor: 'pointer' }}
                      onClick={() => toggle(u)}>
                      <Avatar user={u} size={40} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>{u.name}</div>
                        <div style={{ fontSize: 12, color: '#667781' }}>{u.phone}</div>
                      </div>
                      <div style={{ width: 22, height: 22, borderRadius: '50%', border: `2px solid ${isSel ? 'var(--primary)' : '#ccc'}`, background: isSel ? 'var(--primary)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 14 }}>
                        {isSel ? '✓' : ''}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <>
              <div style={{ textAlign: 'center', marginBottom: 16 }}>
                <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, margin: '0 auto 12px' }}>👥</div>
              </div>
              <div className="form-group">
                <label className="form-label">Group Name *</label>
                <input className="form-input" placeholder="Enter group name" value={groupName}
                  onChange={e => setGroupName(e.target.value)} autoFocus />
              </div>
              <p style={{ fontSize: 13, color: '#667781' }}>{selected.length} participant{selected.length !== 1 ? 's' : ''} selected</p>
            </>
          )}
        </div>
        <div className="modal-footer">
          {step === 1
            ? <button className="btn btn-primary" onClick={() => setStep(2)} disabled={selected.length === 0}>Next →</button>
            : <>
                <button className="btn btn-ghost" onClick={() => setStep(1)}>← Back</button>
                <button className="btn btn-primary" onClick={create} disabled={creating || !groupName.trim()}>
                  {creating ? '⏳ Creating...' : '✅ Create Group'}
                </button>
              </>
          }
        </div>
      </div>
    </div>
  );
}

// ── MY PROFILE MODAL ──────────────────────────────────────────
export function MyProfileModal({ onClose }) {
  const { user, updateUser }   = useAuth();
  const [form, setForm]        = useState({ name: user?.name || '', about: user?.about || '', email: user?.email || '' });
  const [avatar, setAvatar]    = useState(null);
  const [preview, setPreview]  = useState(user?.avatar || '');
  const [saving, setSaving]    = useState(false);
  const [msg, setMsg]          = useState('');

  const handleSave = async () => {
    if (!form.name.trim()) { setMsg('❌ Name is required'); return; }
    setSaving(true);
    setMsg('');
    try {
      const res = await authAPI.updateProfile({ ...form, avatar });
      if (res.data.success) {
        const newUser = res.data.data.user;
        if (newUser) {
          updateUser(newUser);
          // Sync form state with updated data from server
          setForm({ 
            name: newUser.name || '', 
            about: newUser.about || '', 
            email: newUser.email || '' 
          });
        }
        setMsg('✅ Profile updated!');
        setTimeout(() => setMsg(''), 2500);
      } else {
        setMsg('❌ ' + (res.data.message || 'Update failed'));
      }
    } catch (err) { 
      setMsg('❌ ' + (err.response?.data?.message || 'Network error')); 
    }
    setSaving(false);
  };

  const handleAvatar = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAvatar(file);
    setPreview(URL.createObjectURL(file));
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2>👤 My Profile</h2>
          <button className="icon-btn dark" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              {preview
                ? <img src={preview} alt="avatar" style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', border: '3px solid var(--primary)' }} />
                : <Avatar user={user} size={80} />
              }
              <label style={{ position: 'absolute', bottom: 0, right: 0, width: 28, height: 28, background: 'var(--primary)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 14, color: 'white' }}>
                ✏️
                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatar} />
              </label>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Name</label>
            <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">About</label>
            <input className="form-input" value={form.about} onChange={e => setForm({ ...form, about: e.target.value })} maxLength={200} />
            <div style={{ fontSize: 11, color: '#667781', textAlign: 'right' }}>{(form.about || '').length}/200</div>
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Phone</label>
            <input className="form-input" value={user?.phone || ''} disabled style={{ background: '#F5F6F6' }} />
            <div style={{ fontSize: 11, color: '#999' }}>Phone number cannot be changed</div>
          </div>
          {msg && <div style={{ background: msg.startsWith('✅') ? '#E8F5E9' : '#FEE2E2', borderRadius: 8, padding: '8px 12px', fontSize: 13, color: msg.startsWith('✅') ? 'var(--primary)' : '#B91C1C', marginBottom: 8 }}>{msg}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? '⏳ Saving...' : '💾 Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── SETTINGS MODAL ────────────────────────────────────────────
export function SettingsModal({ onClose }) {
  const { logout }             = useAuth();
  const [tab, setTab]          = useState('account');
  const [pwForm, setPwForm]    = useState({ current: '', new: '', confirm: '' });
  const [pwMsg, setPwMsg]      = useState('');
  const [changing, setChanging] = useState(false);

  // Privacy state
  const [privacy, setPrivacy]  = useState({
    last_seen: 'everyone', photo: 'everyone', about: 'everyone', read_receipts: true,
    notif_messages: true, notif_groups: true, notif_calls: true, notif_status: true,
  });
  const [privacyLoading, setPrivacyLoading] = useState(false);
  const [privacyMsg, setPrivacyMsg]         = useState('');

  // Load privacy settings when tab opens
  useEffect(() => {
    if (tab === 'privacy' || tab === 'notifications') {
      authAPI.getPrivacy().then(res => {
        if (res.data.success) {
          const s = res.data.data.settings;
          setPrivacy({
            last_seen:      s.last_seen_privacy || 'everyone',
            photo:          s.photo_privacy     || 'everyone',
            about:          s.about_privacy     || 'everyone',
            read_receipts:  !!s.read_receipts,
            notif_messages: !!s.notif_messages,
            notif_groups:   !!s.notif_groups,
            notif_calls:    !!s.notif_calls,
            notif_status:   !!s.notif_status,
          });
        }
      }).catch(() => {});
    }
  }, [tab]);

  const handlePw = async (e) => {
    e.preventDefault();
    if (pwForm.new !== pwForm.confirm) { setPwMsg('❌ Passwords do not match'); return; }
    setChanging(true);
    try {
      const res = await authAPI.changePassword({ current_password: pwForm.current, new_password: pwForm.new });
      setPwMsg(res.data.success ? '✅ Password changed!' : `❌ ${res.data.message}`);
      if (res.data.success) setPwForm({ current: '', new: '', confirm: '' });
    } catch { setPwMsg('❌ Network error'); }
    setChanging(false);
  };

  const savePrivacy = async () => {
    setPrivacyLoading(true);
    try {
      const res = await authAPI.updatePrivacy({
        last_seen:      privacy.last_seen,
        photo:          privacy.photo,
        about:          privacy.about,
        read_receipts:  privacy.read_receipts  ? 1 : 0,
        notif_messages: privacy.notif_messages ? 1 : 0,
        notif_groups:   privacy.notif_groups   ? 1 : 0,
        notif_calls:    privacy.notif_calls    ? 1 : 0,
        notif_status:   privacy.notif_status   ? 1 : 0,
      });
      setPrivacyMsg(res.data.success ? '✅ Settings saved!' : '❌ ' + res.data.message);
    } catch { setPrivacyMsg('❌ Network error'); }
    setPrivacyLoading(false);
    setTimeout(() => setPrivacyMsg(''), 2500);
  };

  const privacySelect = (key, value) => setPrivacy(p => ({ ...p, [key]: value }));
  const privacyToggle = (key) => setPrivacy(p => ({ ...p, [key]: !p[key] }));

  const Toggle = ({ value, onChange }) => (
    <label style={{ position: 'relative', display: 'inline-block', width: 44, height: 24, cursor: 'pointer' }}>
      <input type="checkbox" checked={value} onChange={onChange} style={{ opacity: 0, width: 0, height: 0 }} />
      <span style={{
        position: 'absolute', cursor: 'pointer', inset: 0,
        background: value ? 'var(--primary)' : '#ccc',
        borderRadius: 34, transition: '.3s',
      }}>
        <span style={{
          position: 'absolute', height: 18, width: 18, left: value ? 22 : 3, bottom: 3,
          background: 'white', borderRadius: '50%', transition: '.3s',
        }} />
      </span>
    </label>
  );

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 500 }}>
        <div className="modal-header">
          <h2>⚙️ Settings</h2>
          <button className="icon-btn dark" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: '#F5F6F6', borderRadius: 10, padding: 4 }}>
            {['account', 'privacy', 'notifications'].map(t => (
              <button key={t} onClick={() => setTab(t)}
                style={{
                  flex: 1, padding: '7px 6px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  fontWeight: 600, fontSize: 13, textTransform: 'capitalize', transition: 'all .2s',
                  background: tab === t ? 'white' : 'transparent',
                  color: tab === t ? 'var(--primary)' : '#667781',
                  boxShadow: tab === t ? '0 1px 4px rgba(0,0,0,.12)' : 'none',
                }}>
                {t === 'account' ? '🔐 Account' : t === 'privacy' ? '🔒 Privacy' : '🔔 Notifications'}
              </button>
            ))}
          </div>

          {/* ACCOUNT TAB */}
          {tab === 'account' && (
            <div>
              <form onSubmit={handlePw}>
                <h4 style={{ fontWeight: 700, marginBottom: 12, fontSize: 15 }}>Change Password</h4>
                <div className="form-group">
                  <label className="form-label">Current Password</label>
                  <input className="form-input" type="password" value={pwForm.current}
                    onChange={e => setPwForm({ ...pwForm, current: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">New Password</label>
                  <input className="form-input" type="password" value={pwForm.new}
                    onChange={e => setPwForm({ ...pwForm, new: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Confirm New Password</label>
                  <input className="form-input" type="password" value={pwForm.confirm}
                    onChange={e => setPwForm({ ...pwForm, confirm: e.target.value })} required />
                </div>
                {pwMsg && <p style={{ fontSize: 13, marginBottom: 8, color: pwMsg.startsWith('✅') ? 'var(--primary)' : '#E74C3C' }}>{pwMsg}</p>}
                <button className="btn btn-primary" type="submit" disabled={changing}>
                  {changing ? '⏳ Changing...' : '🔐 Change Password'}
                </button>
              </form>
              <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid var(--border)' }} />
              <button className="btn btn-danger" style={{ width: '100%', fontWeight: 700, letterSpacing: '0.5px' }} onClick={() => { 
                if (window.confirm('Are you sure you want to log out of your session?')) {
                  logout(); 
                  onClose(); 
                }
              }}>
                🚪 Log Out
              </button>
            </div>
          )}

          {/* PRIVACY TAB */}
          {tab === 'privacy' && (
            <div>
              <h4 style={{ fontWeight: 700, marginBottom: 12 }}>Privacy Settings</h4>
              {[
                { label: 'Last seen', desc: 'Who can see when you were last online', key: 'last_seen' },
                { label: 'Profile photo', desc: 'Who can see your profile photo', key: 'photo' },
                { label: 'About', desc: 'Who can see your About info', key: 'about' },
              ].map(item => (
                <div key={item.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{item.label}</div>
                    <div style={{ fontSize: 12, color: '#667781' }}>{item.desc}</div>
                  </div>
                  <select
                    value={privacy[item.key]}
                    onChange={e => privacySelect(item.key, e.target.value)}
                    style={{ padding: '5px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, background: 'white', cursor: 'pointer' }}>
                    <option value="everyone">Everyone</option>
                    <option value="contacts">Contacts only</option>
                    <option value="nobody">Nobody</option>
                  </select>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>Read receipts</div>
                  <div style={{ fontSize: 12, color: '#667781' }}>Show blue ticks when messages are read</div>
                </div>
                <Toggle value={privacy.read_receipts} onChange={() => privacyToggle('read_receipts')} />
              </div>
              {privacyMsg && <div style={{ marginTop: 10, fontSize: 13, color: privacyMsg.startsWith('✅') ? 'var(--primary)' : '#E74C3C' }}>{privacyMsg}</div>}
              <button className="btn btn-primary" style={{ marginTop: 16, width: '100%' }} onClick={savePrivacy} disabled={privacyLoading}>
                {privacyLoading ? '⏳ Saving...' : '💾 Save Privacy Settings'}
              </button>
            </div>
          )}

          {/* NOTIFICATIONS TAB */}
          {tab === 'notifications' && (
            <div>
              <h4 style={{ fontWeight: 700, marginBottom: 12 }}>Notification Settings</h4>
              {[
                { label: 'Message notifications', icon: '💬', key: 'notif_messages' },
                { label: 'Group notifications',   icon: '👥', key: 'notif_groups' },
                { label: 'Call notifications',    icon: '📞', key: 'notif_calls' },
                { label: 'Status notifications',  icon: '⭕', key: 'notif_status' },
              ].map(item => (
                <div key={item.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 0', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ fontSize: 14, fontWeight: 500 }}>{item.icon} {item.label}</span>
                  <Toggle value={privacy[item.key]} onChange={() => privacyToggle(item.key)} />
                </div>
              ))}
              {privacyMsg && <div style={{ marginTop: 10, fontSize: 13, color: privacyMsg.startsWith('✅') ? 'var(--primary)' : '#E74C3C' }}>{privacyMsg}</div>}
              <button className="btn btn-primary" style={{ marginTop: 16, width: '100%' }} onClick={savePrivacy} disabled={privacyLoading}>
                {privacyLoading ? '⏳ Saving...' : '💾 Save Notification Settings'}
              </button>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
